import { useState, useEffect } from 'react'
import { Outlet } from 'react-router-dom'
import Sidebar from './Sidebar'
import TopBar from './TopBar'
import MobileTabBar from './MobileTabBar'
import GlobalSearch from '@/components/ui/GlobalSearch'
import { useNetwork } from '@/hooks/useNetwork'
import { useCapacitor } from '@/hooks/useCapacitor'
import { useAppStore } from '@/store/useAppStore'
import { CheckCircle2, Loader2, X } from 'lucide-react'
import OnboardingChecklist from '@/components/onboarding/OnboardingChecklist'
import { useSubscription } from '@/hooks/useSubscription'

export default function Layout() {
  const [mobileOpen, setMobileOpen] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const { isTrialing, trialDaysLeft, isPastDue, openPortal } = useSubscription()
  const { isOnline } = useNetwork()
  const { isNative } = useCapacitor()
  const { backgroundImport, setBackgroundImport } = useAppStore()

  // Auto-dismiss après 6s quand terminé
  useEffect(() => {
    if (backgroundImport?.status === 'done' || backgroundImport?.status === 'error') {
      const t = setTimeout(() => setBackgroundImport(null), 6000)
      return () => clearTimeout(t)
    }
  }, [backgroundImport?.status, setBackgroundImport])

  // CMD+K / CTRL+K global shortcut
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setSearchOpen((v) => !v)
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [])

  return (
    <div className="flex h-screen overflow-hidden bg-[#F8F9FC] dark:bg-slate-950">
      {/* Backdrop mobile — ferme la sidebar au clic */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-black/40 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <Sidebar mobileOpen={mobileOpen} onClose={() => setMobileOpen(false)} />

      <div className="flex-1 flex flex-col overflow-hidden min-w-0">
        <TopBar onMenuClick={() => setMobileOpen(true)} onSearchOpen={() => setSearchOpen(true)} />

        {/* Bandeau hors-ligne */}
        {!isOnline && (
          <div className="bg-amber-500 text-white text-xs font-semibold text-center py-1.5 px-4 shrink-0">
            Vous êtes hors-ligne — certaines fonctionnalités peuvent être indisponibles
          </div>
        )}

        {/* Bannière trial */}
        {isTrialing && (
          <div className={`text-center px-4 py-1.5 text-xs font-medium shrink-0 ${
            trialDaysLeft <= 3 ? 'bg-red-500 text-white' : 'bg-[#4F46E5] text-white'
          }`}>
            {trialDaysLeft <= 3 && trialDaysLeft > 0
              ? `⚠️ Votre essai se termine dans ${trialDaysLeft} jour${trialDaysLeft > 1 ? 's' : ''} — pensez à choisir un plan`
              : trialDaysLeft === 0
              ? '⚠️ Votre essai gratuit est terminé'
              : `⏳ Essai gratuit — ${trialDaysLeft} jour${trialDaysLeft > 1 ? 's' : ''} restant${trialDaysLeft > 1 ? 's' : ''}`
            }
          </div>
        )}

        {/* Bannière paiement en retard */}
        {isPastDue && (
          <div className="flex items-center justify-between px-4 py-2 text-xs font-medium bg-red-600 text-white shrink-0">
            <span>⚠️ Paiement en retard — votre accès sera suspendu prochainement</span>
            <button
              onClick={() => openPortal().catch(() => {})}
              className="text-xs bg-white/20 hover:bg-white/30 px-2.5 py-1 rounded-full font-semibold transition-colors shrink-0 ml-4"
            >
              Mettre à jour →
            </button>
          </div>
        )}

        <main
          className="flex-1 overflow-y-auto p-4 md:p-6"
          style={{ paddingBottom: isNative ? 'calc(env(safe-area-inset-bottom, 0px) + 64px)' : undefined }}
        >
          <Outlet />
        </main>
      </div>

      <MobileTabBar />
      <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
      <OnboardingChecklist />

      {/* ── Puce import en arrière-plan ─────────────────────────────────────── */}
      {backgroundImport && (
        <div className="fixed bottom-20 md:bottom-6 right-4 z-50 flex items-center gap-3 px-4 py-3 rounded-2xl shadow-xl border bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 max-w-xs">
          {backgroundImport.status === 'running' ? (
            <Loader2 size={16} className="text-[#4F46E5] animate-spin shrink-0" />
          ) : backgroundImport.status === 'done' ? (
            <CheckCircle2 size={16} className="text-emerald-500 shrink-0" />
          ) : (
            <span className="text-red-500 shrink-0 text-sm">⚠️</span>
          )}
          <div className="flex-1 min-w-0">
            <p className="text-xs font-semibold text-gray-900 dark:text-white truncate">
              {backgroundImport.label}
            </p>
            {backgroundImport.status === 'running' && (
              <>
                <div className="mt-1 h-1 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-[#4F46E5] rounded-full transition-all duration-300"
                    style={{ width: `${Math.round((backgroundImport.done / backgroundImport.total) * 100)}%` }}
                  />
                </div>
                <p className="text-[10px] text-gray-400 mt-0.5">
                  {backgroundImport.done} / {backgroundImport.total} profils
                </p>
              </>
            )}
            {backgroundImport.status === 'done' && (
              <p className="text-[10px] text-emerald-600 mt-0.5">
                {backgroundImport.done} profil{backgroundImport.done !== 1 ? 's' : ''} importé{backgroundImport.done !== 1 ? 's' : ''} ✓
              </p>
            )}
            {backgroundImport.status === 'error' && (
              <p className="text-[10px] text-red-500 mt-0.5 truncate">{backgroundImport.error}</p>
            )}
          </div>
          {backgroundImport.status !== 'running' && (
            <button
              onClick={() => setBackgroundImport(null)}
              className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 transition shrink-0"
            >
              <X size={12} />
            </button>
          )}
        </div>
      )}
    </div>
  )
}
